import type { FormEvent } from 'react';
import { ErreurApi, type Client } from '../api/client.js';
import type { Session } from '../api/contrat.js';
import { LogoMaat } from './maat.js';
import { COULEURS, IDENTITE, OMBRES, POLICE_EQUIPE, POLICE_TITRES, RAYONS, styleBoutonPrincipal } from './styles.js';

/**
 * Connexion de l’équipe en deux étapes : adresse e-mail et mot de passe, puis le code à 6 chiffres (TOTP)
 * seulement quand le serveur a reconnu les identifiants et le réclame (OTP_REQUIRED).
 */
export interface SaisieConnexion {
  email: string;
  motDePasse: string;
  totp: string;
}

export interface ErreursConnexion {
  email?: string;
  motDePasse?: string;
  totp?: string;
}

const ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validerConnexion(saisie: SaisieConnexion): { ok: boolean; erreurs: ErreursConnexion } {
  const erreurs: ErreursConnexion = {};
  const email = saisie.email.trim();
  if (email === '') erreurs.email = 'Saisissez votre adresse e-mail.';
  else if (!ADRESSE.test(email)) erreurs.email = 'Saisissez une adresse e-mail valide, par exemple prenom@exemple.fr.';
  if (saisie.motDePasse === '') erreurs.motDePasse = 'Saisissez votre mot de passe.';
  else if (saisie.motDePasse.length < 8) erreurs.motDePasse = 'Votre mot de passe comporte au moins 8 caractères.';
  if (saisie.totp !== '' && !/^\d{6}$/.test(saisie.totp)) {
    erreurs.totp = 'Saisissez le code à 6 chiffres de votre application, ou laissez ce champ vide.';
  }
  return { ok: Object.keys(erreurs).length === 0, erreurs };
}

export type EtapeConnexion = 'identifiants' | 'code';

export type ResultatConnexion =
  | { ok: true; session: Session }
  | { ok: false; codeRequis: true; message: null; erreurs?: undefined }
  | { ok: false; message: string; erreurs?: ErreursConnexion };

const ERREUR_CODE = 'Saisissez le code à 6 chiffres affiché par votre application.';
export const MESSAGE_CODE_REFUSE = 'Ce code n’est pas valable ou a expiré. Saisissez le nouveau code affiché par votre application.';

/** Le code de la seconde étape est obligatoire et fait exactement 6 chiffres. */
export const validerCode = (code: string): string | null => (/^\d{6}$/.test(code) ? null : ERREUR_CODE);

const MESSAGE_REFUS = 'Impossible de vous connecter : adresse e-mail ou mot de passe incorrect. Vérifiez votre saisie, puis réessayez.';

export async function soumettreConnexion(client: Client, saisie: SaisieConnexion): Promise<ResultatConnexion> {
  const { ok, erreurs } = validerConnexion(saisie);
  if (!ok) return { ok: false, message: 'Certaines informations sont à corriger avant de continuer.', erreurs };
  const corps = {
    email: saisie.email.trim(),
    password: saisie.motDePasse,
    ...(saisie.totp !== '' ? { totp: saisie.totp } : {}),
  };
  try {
    return { ok: true, session: await client.appeler<Session>('auth.connexion', { corps }) };
  } catch (e) {
    if (e instanceof ErreurApi) {
      if (e.code === 'OTP_REQUIRED') return { ok: false, codeRequis: true, message: null };
      if (e.code === 'UNAUTHENTICATED') return { ok: false, message: saisie.totp !== '' ? MESSAGE_CODE_REFUSE : MESSAGE_REFUS };
      return { ok: false, message: e.messageUtilisateur };
    }
    throw e;
  }
}

export const saisieDepuisFormulaire = (donnees: FormData): SaisieConnexion => ({
  email: String(donnees.get('email') ?? ''),
  motDePasse: String(donnees.get('motDePasse') ?? ''),
  totp: String(donnees.get('totp') ?? '').trim(),
});

export interface PropsPageConnexion {
  onSoumettre: (saisie: SaisieConnexion) => void;
  enCours: boolean;
  erreurs: ErreursConnexion;
  message: string | null;
  etape?: EtapeConnexion;
  onChangerAdresse?: () => void;
}

const champ = { display: 'grid', gap: 4, marginBottom: 16 } as const;
const entree = {
  minHeight: 44,
  padding: '0 12px',
  font: 'inherit',
  border: `1px solid ${COULEURS.ardoise}`,
  borderRadius: RAYONS.bouton,
} as const;
const erreurTexte = { color: COULEURS.rouge, margin: 0 } as const;
const boutonLien = {
  minHeight: 44,
  marginTop: 8,
  padding: 0,
  background: 'none',
  border: 'none',
  color: COULEURS.marine,
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const;

export function PageConnexion({ onSoumettre, enCours, erreurs, message, etape = 'identifiants', onChangerAdresse }: PropsPageConnexion) {
  const code = etape === 'code';
  const soumettre = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSoumettre(saisieDepuisFormulaire(new FormData(e.currentTarget)));
  };
  const lien = (nom: keyof ErreursConnexion) =>
    erreurs[nom] ? { 'aria-invalid': true, 'aria-describedby': `connexion-${nom}-erreur` } : {};
  return (
    <div
      data-page="connexion"
      style={{ minHeight: '100vh', background: COULEURS.surface, fontFamily: POLICE_EQUIPE, color: COULEURS.marineFonce, lineHeight: 1.5 }}
    >
      <style>{'body{margin:0}'}</style>
      <header
        data-bandeau="maat"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 56,
          padding: '8px 24px',
          background: COULEURS.marine,
          borderBottom: `3px solid ${IDENTITE.orange}`,
          color: COULEURS.blanc,
        }}
      >
        <LogoMaat />
        <b style={{ letterSpacing: '0.06em', fontWeight: 600, fontSize: 20 }}>MAAT</b>
        <span style={{ fontSize: 15, opacity: 0.9 }}>Lakou</span>
      </header>
      <main style={{ padding: '32px 16px 48px' }}>
        <div
          data-carte-connexion=""
          style={{
            boxSizing: 'border-box',
            maxWidth: 440,
            margin: '0 auto',
            padding: 32,
            background: COULEURS.blanc,
            border: `1px solid ${COULEURS.bordureDouce}`,
            borderRadius: RAYONS.carte,
            boxShadow: OMBRES.carte,
          }}
        >
          <h1 style={{ color: COULEURS.marine, fontSize: 28, fontFamily: POLICE_TITRES, fontWeight: 600, margin: '0 0 8px' }}>Connexion</h1>
          <p>
            {code
              ? 'Saisissez le code à 6 chiffres affiché par votre application d’authentification.'
              : 'Connectez-vous pour retrouver vos dossiers de succession.'}
          </p>
          {message ? (
            <p role="alert" style={erreurTexte}>
              {message}
            </p>
          ) : null}
          <form onSubmit={soumettre} noValidate>
            {code ? null : (
              <>
                <div style={champ}>
                  <label htmlFor="connexion-email">Adresse e-mail</label>
                  <input
                    id="connexion-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    required
                    style={entree}
                    {...lien('email')}
                  />
                  {erreurs.email ? (
                    <p id="connexion-email-erreur" style={erreurTexte}>
                      {erreurs.email}
                    </p>
                  ) : null}
                </div>
                <div style={champ}>
                  <label htmlFor="connexion-motDePasse">Mot de passe</label>
                  <input
                    id="connexion-motDePasse"
                    name="motDePasse"
                    type="password"
                    autoComplete="current-password"
                    required
                    style={entree}
                    {...lien('motDePasse')}
                  />
                  {erreurs.motDePasse ? (
                    <p id="connexion-motDePasse-erreur" style={erreurTexte}>
                      {erreurs.motDePasse}
                    </p>
                  ) : null}
                </div>
              </>
            )}
            {code ? (
              <div style={champ}>
                <label htmlFor="connexion-totp">Code à 6 chiffres</label>
                <input
                  id="connexion-totp"
                  name="totp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  style={entree}
                  {...lien('totp')}
                />
                {erreurs.totp ? (
                  <p id="connexion-totp-erreur" style={erreurTexte}>
                    {erreurs.totp}
                  </p>
                ) : null}
              </div>
            ) : null}
            <button type="submit" data-bouton-principal disabled={enCours} style={styleBoutonPrincipal}>
              {enCours ? 'Connexion en cours…' : code ? 'Valider' : 'Se connecter'}
            </button>
          </form>
          {code ? (
            <button type="button" onClick={onChangerAdresse} style={boutonLien}>
              Changer d’adresse e-mail
            </button>
          ) : null}
          <p style={{ color: COULEURS.ardoise }}>Cabinet MAAT</p>
        </div>
      </main>
    </div>
  );
}
